import { Fragment, useState } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { classNames } from '@progress/kendo-react-common'
import { ClientSelectorProps, MembershipFilters } from '../common'
import SearchBox from 'components/SearchBox'
import Translation from 'components/Translation'

export function FilterSelector(props: ClientSelectorProps) {
    const [is_opened, openMenu] = useState(false)
    const [is_membership_filter_opened, toggleMembershipFilter] =
        useState(true)
    const [is_availability_opened, toggleAvailabilityFilter] =
        useState(true)
    const [membership_status, setMembershipStatusFilter] = useState<
        number[]
    >([])
    const [availability, setAvailabilityFilter] = useState<number[]>([])

    function handleAvailabilityStatusFilter(
        filter: string,
    ) {
        props.setIsFiltered?.(filter === "UPCOMING")
    }

    function handleMembershipStatusFilter(
        filter: MembershipFilters
    ) {
        props.setMembershipFilter?.(filter)
    }

    return (
        <Menu
            as="div"
            className={classNames(
                'relative inline-block text-left',
                props.className
            )}
        >
            <div>
                <Menu.Button
                    className={classNames(
                        'inline-flex justify-center items-center w-full',
                        'rounded-md border border-gray-150 shadow-sm p-2 bg-white',
                        'text-sm font-medium text-gray-700',
                        'hover:bg-gray-50 '
                    )}
                    onClickCapture={() => {
                        openMenu(!is_opened)
                    }}
                >
                    <i
                        className="feather-filter text-sm mr-2"
                        aria-hidden="true"
                    />
                    <span>Filter</span>
                    <i
                        className="feather-chevron-down ml-2 text-sm text-primary-light"
                        aria-hidden="true"
                    />
                </Menu.Button>
            </div>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
                show={is_opened}
            >
                <Menu.Items
                    static
                    className={classNames(
                        props.position,
                        'absolute mt-2 w-max px-3',
                        'rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5',
                        'focus:outline-none'
                    )}
                >
                    <div
                        className="py-4 px-2"
                        style={{ minHeight: '170px' }}
                    >
                        { props.is_search_enabled &&
                            <Menu.Item>
                                <SearchBox />
                            </Menu.Item>
                        }
                        { props.is_membership_filter_enabled &&
                            <Menu.Item as="div">
                                <button
                                    type="button"
                                    onClick={() => {
                                        toggleMembershipFilter(
                                            !is_membership_filter_opened
                                        )
                                    }}
                                    className="rounded-md border-gray-200 border font-medium font-sans px-3 py-2 mb-3 mt-5"
                                >
                                    <span className="mr-2">Memberships</span>
                                    <i
                                        className={
                                            is_membership_filter_opened
                                                ? 'feather-chevron-down'
                                                : 'feather-chevron-up'
                                        }
                                    />
                                </button>

                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 -translate-y-4"
                                    enterTo="transform opacity-100 translate-y-0"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 translate-y-0"
                                    leaveTo="transform opacity-0 -translate-y-4"
                                    show={is_membership_filter_opened}
                                >
                                    <div className="flex">
                                        <label
                                            htmlFor="filter-membership-all"
                                            className="flex min-w-0 items-center text-gray-500 h-full px-3 py-2"
                                        >
                                            <input
                                                id="filter-membership-all"
                                                name="filter_membership[]"
                                                type="radio"
                                                value="all"
                                                disabled={props.is_loading}
                                                checked={props.membership_filter === MembershipFilters.ALL}
                                                onChange={(e) => {
                                                    handleMembershipStatusFilter(
                                                        MembershipFilters.ALL
                                                    )
                                                }}
                                                className="form-radio h-4 w-4 mr-2"
                                            />
                                            <Translation content_key="all" />
                                        </label>

                                        <label
                                            htmlFor="filter-membership-active"
                                            className="flex min-w-0 items-center text-gray-500 h-full px-3 py-2"
                                        >
                                            <input
                                                id="filter-membership-active"
                                                name="filter_membership[]"
                                                type="radio"
                                                value="active"
                                                disabled={props.is_loading}
                                                checked={props.membership_filter === MembershipFilters.ACTIVE}
                                                onChange={(e) => {
                                                    handleMembershipStatusFilter(
                                                        MembershipFilters.ACTIVE
                                                    )
                                                }}
                                                className="form-radio h-4 w-4 mr-2"
                                            />
                                            <Translation content_key="active" />
                                        </label>

                                        <label
                                            htmlFor="filter-membership-inactive"
                                            className="flex min-w-0 items-center text-gray-500 h-full px-3 py-2"
                                        >
                                            <input
                                                id="filter-membership-inactive"
                                                name="filter_membership[]"
                                                type="radio"
                                                value="expired"
                                                disabled={props.is_loading}
                                                checked={props.membership_filter === MembershipFilters.INACTIVE}
                                                onChange={(e) => {
                                                    handleMembershipStatusFilter(
                                                        MembershipFilters.INACTIVE
                                                    )
                                                }}
                                                className="form-radio h-4 w-4 mr-2"
                                            />
                                            <Translation content_key="inactive" />
                                        </label>
                                        <label
                                            htmlFor="filter-membership-retrying"
                                            className="flex min-w-0 items-center text-gray-500 h-full px-3 py-2"
                                        >
                                            <input
                                                id="filter-membership-retrying"
                                                name="filter_membership[]"
                                                type="radio"
                                                value="expired"
                                                disabled={props.is_loading}
                                                checked={props.membership_filter === MembershipFilters.RETRYING}
                                                onChange={(e) => {
                                                    handleMembershipStatusFilter(
                                                        MembershipFilters.RETRYING
                                                    )
                                                }}
                                                className="form-radio h-4 w-4 mr-2"
                                            />
                                            <Translation content_key="retrying" />
                                        </label>
                                    </div>
                                </Transition>
                            </Menu.Item>
                        }
                        { props.is_appointment_filter_enabled &&
                            <Menu.Item as="div">
                                <button
                                    type="button"
                                    onClick={() => {
                                        toggleAvailabilityFilter(
                                            !is_availability_opened
                                        )
                                    }}
                                    className="rounded-md border-gray-200 border font-medium font-sans px-3 py-2 mb-3 mt-5"
                                >
                                    <span className="mr-2">
                                        Upcoming Appointments
                                    </span>
                                    <i
                                        className={
                                            is_availability_opened
                                                ? 'feather-chevron-down'
                                                : 'feather-chevron-up'
                                        }
                                    />
                                </button>

                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 -translate-y-4"
                                    enterTo="transform opacity-100 translate-y-0"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 translate-y-0"
                                    leaveTo="transform opacity-0 -translate-y-4"
                                    show={is_availability_opened}
                                >
                                    <div className="flex">
                                        <label
                                            htmlFor="filter-availability-today"
                                            className="flex min-w-0 items-center text-gray-500 h-full px-3 py-2"
                                        >
                                            <input
                                                id="filter-availability-today"
                                                name="filter_availability[]"
                                                type="radio"
                                                value="today"
                                                disabled={props.is_loading}
                                                checked={props.is_filtered}
                                                onChange={(e) => {
                                                    handleAvailabilityStatusFilter(
                                                        "UPCOMING"
                                                    )
                                                }}
                                                className="form-radio h-4 w-4 mr-2"
                                            />
                                            <Translation content_key="upcoming_appointments" />
                                        </label>

                                        <label
                                            htmlFor="filter-availability-dg"
                                            className="flex min-w-0 items-center text-gray-500 h-full px-3 py-2"
                                        >
                                            <input
                                                id="filter-availability-dg"
                                                name="filter_availability[]"
                                                type="radio"
                                                value="dg"
                                                disabled={props.is_loading}
                                                checked={!props.is_filtered}
                                                onChange={(e) => {
                                                    handleAvailabilityStatusFilter(
                                                        "ALL"
                                                    )
                                                }}
                                                className="form-radio h-4 w-4 mr-2"
                                            />
                                            <Translation content_key="all_appointments" />
                                        </label>
                                    </div>
                                </Transition>
                            </Menu.Item>
                        }
                    </div>
                </Menu.Items>
            </Transition>
        </Menu>
    )
}

export default FilterSelector
